import { useRef, useState, useCallback, useEffect } from 'react'
import { TaskListItem, TaskType } from '@myapp/contract'
import { TIMELINE_DURATION_MINUTES, TIMELINE_START_HOUR, getDateIsoFromXPercent } from '../../../shared/lib/timeline'
import { isMidnightScheduledDate } from '../../../shared/lib/timeline/dateHelpers'
import { DefaultDurations } from '../../../shared/lib/blockRanges'

export interface TaskDragState {
  taskId: number
  taskType: TaskType
  taskTitle: string
  taskStatus: TaskListItem['status']
  originTechnicianId: string
  currentTechnicianId: string
  anfahrtStartIso: string
  isDayLevel: boolean
  isColliding: boolean
  isSaving: boolean
  durations: {
    plannedTravelDuration: number
    plannedWorkDuration: number
    plannedDepartureDuration: number
  }
}

interface DragRef {
  state: TaskDragState
  offsetMinutes: number
}

type HasCollisionFn = (iso: string, dur: DefaultDurations, techId: string, excludeId?: string, excludeTaskId?: number) => boolean

interface Options {
  dayIso: string
  hasCollision?: HasCollisionFn
  technicians?: { technicianId: string; workingDays: number[]; shiftStart: string | null; shiftEnd: string | null }[]
  onTaskDropped?: (taskId: number, update: { plannedStart: string; assignedTechnicianId: number }) => Promise<void>
  confirmBeforeDrop?: (taskStatus: TaskListItem['status']) => boolean | Promise<boolean>
}

export function useTaskDrag({ dayIso, hasCollision, technicians, onTaskDropped, confirmBeforeDrop }: Options) {
  const dragRef = useRef<DragRef | null>(null)
  const [dragState, setDragState] = useState<TaskDragState | null>(null)

  const isWithinShift = useCallback(
    (anfahrtStartIso: string, durations: DefaultDurations, technicianId: string): boolean => {
      const tech = technicians?.find((t) => t.technicianId === technicianId)
      if (!tech) return true
      const dow = new Date(dayIso).getDay()
      const mapped = dow === 0 ? 7 : dow
      if (!tech.workingDays.includes(mapped)) return false
      if (!tech.shiftStart || !tech.shiftEnd) return true
      const shiftStartH = new Date(tech.shiftStart).getUTCHours() + new Date(tech.shiftStart).getUTCMinutes() / 60
      const shiftEndH = new Date(tech.shiftEnd).getUTCHours() + new Date(tech.shiftEnd).getUTCMinutes() / 60
      const blockStartH = new Date(anfahrtStartIso).getHours() + new Date(anfahrtStartIso).getMinutes() / 60
      const totalMin = durations.plannedTravelDuration + durations.plannedWorkDuration + durations.plannedDepartureDuration
      const blockEndH = blockStartH + totalMin / 60
      return blockStartH >= shiftStartH && blockEndH <= shiftEndH
    },
    [technicians, dayIso],
  )

  const findTrackRect = (technicianId: string): DOMRect | null => {
    const tracks = document.querySelectorAll<HTMLElement>('[data-timeline-track]')
    for (const track of Array.from(tracks)) {
      if (track.dataset.technicianId === technicianId) {
        return track.getBoundingClientRect()
      }
    }
    return null
  }

  const startDrag = useCallback(
    (task: TaskListItem, technicianId: string, e: React.MouseEvent) => {
      if (!task.plannedStart) return
      e.preventDefault()
      e.stopPropagation()

      const isStartingDayLevel = isMidnightScheduledDate(task.plannedStart)

      const trackRect = findTrackRect(technicianId)
      if (!trackRect) return

      let offsetMinutes = 0
      let anfahrtStartIso: string

      if (isStartingDayLevel) {
        const cursorPercent = ((e.clientX - trackRect.left) / trackRect.width) * 100
        anfahrtStartIso = getDateIsoFromXPercent(cursorPercent, dayIso)
        offsetMinutes = 0
      } else {
        const workStart = new Date(task.plannedStart)
        const anfahrtStart = new Date(workStart.getTime() - task.plannedTravelDuration * 60_000)
        anfahrtStartIso = anfahrtStart.toISOString()

        const cursorPercent = ((e.clientX - trackRect.left) / trackRect.width) * 100
        const cursorMinutes = (cursorPercent / 100) * TIMELINE_DURATION_MINUTES
        const blockStartMinutes = anfahrtStart.getHours() * 60 + anfahrtStart.getMinutes() - TIMELINE_START_HOUR * 60
        offsetMinutes = cursorMinutes - blockStartMinutes
      }

      const state: TaskDragState = {
        taskId: task.id,
        taskType: task.taskType,
        taskTitle: task.title,
        taskStatus: task.status,
        originTechnicianId: technicianId,
        currentTechnicianId: technicianId,
        anfahrtStartIso,
        isDayLevel: isStartingDayLevel,
        isColliding: false,
        isSaving: false,
        durations: {
          plannedTravelDuration: task.plannedTravelDuration,
          plannedWorkDuration: task.plannedWorkDuration,
          plannedDepartureDuration: task.plannedDepartureDuration,
        },
      }
      dragRef.current = { state, offsetMinutes }
      setDragState(state)
    },
    [dayIso],
  )

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!dragRef.current) return
      const { state, offsetMinutes } = dragRef.current

      let isDayLevel = false
      let dayLevelTechId: string | null = null
      if (state.taskType === 'ERRANDS') {
        const zones = document.querySelectorAll<HTMLElement>('[data-day-level-zone]')
        for (const zone of Array.from(zones)) {
          const rect = zone.getBoundingClientRect()
          if (e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom) {
            isDayLevel = true
            dayLevelTechId = zone.dataset.technicianId ?? null
            break
          }
        }
      }

      const tracks = document.querySelectorAll<HTMLElement>('[data-timeline-track]')
      let currentTrack: HTMLElement | null = null
      for (const track of Array.from(tracks)) {
        const rect = track.getBoundingClientRect()
        if (e.clientY >= rect.top && e.clientY <= rect.bottom) {
          currentTrack = track
          break
        }
      }

      let newTechnicianId = state.currentTechnicianId
      let trackRect: DOMRect | null = null

      if (isDayLevel && dayLevelTechId) {
        newTechnicianId = dayLevelTechId
        trackRect = findTrackRect(dayLevelTechId)
      } else if (currentTrack) {
        newTechnicianId = currentTrack.dataset.technicianId ?? state.currentTechnicianId
        trackRect = currentTrack.getBoundingClientRect()
      } else {
        const originTrack = Array.from(tracks).find((t) => t.dataset.technicianId === state.originTechnicianId)
        trackRect = originTrack?.getBoundingClientRect() ?? null
      }

      if (!trackRect) return

      let newAnfahrtStartIso = state.anfahrtStartIso
      if (!isDayLevel) {
        const cursorPercent = ((e.clientX - trackRect.left) / trackRect.width) * 100
        const blockStartPercent = cursorPercent - (offsetMinutes / TIMELINE_DURATION_MINUTES) * 100
        newAnfahrtStartIso = getDateIsoFromXPercent(blockStartPercent, dayIso)
      }

      const isOutsideShift = !isDayLevel && !isWithinShift(newAnfahrtStartIso, state.durations, newTechnicianId)
      const isColliding =
        !isDayLevel && (
          isOutsideShift ||
          (!!hasCollision && hasCollision(newAnfahrtStartIso, state.durations, newTechnicianId, undefined, state.taskId))
        )

      const newState: TaskDragState = {
        ...state,
        currentTechnicianId: newTechnicianId,
        anfahrtStartIso: newAnfahrtStartIso,
        isDayLevel,
        isColliding,
        isSaving: false,
      }
      dragRef.current = { state: newState, offsetMinutes }
      setDragState({ ...newState })
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && dragRef.current) {
        dragRef.current = null
        setDragState(null)
      }
    }

    const handleMouseUp = async () => {
      const drag = dragRef.current
      if (!drag) return
      dragRef.current = null

      const { state } = drag

      if (state.isColliding) {
        setDragState(null)
        return
      }

      if (confirmBeforeDrop) {
        const allowed = await confirmBeforeDrop(state.taskStatus)
        if (!allowed) {
          setDragState(null)
          return
        }
      }

      setDragState({ ...state, isSaving: true })

      try {
        let plannedStart: string
        if (state.isDayLevel) {
          const base = new Date(dayIso)
          plannedStart = new Date(Date.UTC(base.getFullYear(), base.getMonth(), base.getDate())).toISOString()
        } else {
          const workStart = new Date(
            new Date(state.anfahrtStartIso).getTime() + state.durations.plannedTravelDuration * 60_000,
          )
          plannedStart = workStart.toISOString()
        }

        await onTaskDropped?.(state.taskId, {
          plannedStart,
          assignedTechnicianId: Number(state.currentTechnicianId),
        })
      } finally {
        setDragState(null)
      }
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [dayIso, hasCollision, isWithinShift, onTaskDropped, confirmBeforeDrop])

  useEffect(() => {
    if (!dragState) return
    document.body.style.cursor = 'grabbing'
    document.body.style.userSelect = 'none'
    return () => {
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [dragState])

  return { dragState, startDrag }
}
